import React, { useState, useEffect, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Checkbox,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { countryList, getRegionCode } from "../../../utilities/countries";
import { validateAddressWithAPI } from '../../../helpers/addressValidation';
import { addCustomer, clearCustomerMessages, getCustomerOrgAccounts, resetCustomerOrgAccounts } from '../../../store/customer/customerSlice';
import {
  CloudProviderContext,
  buildAccountCloudTypeLookup,
  filterAccountsByCloudProvider,
} from "../../../CloudProviderContext";
import { useAccounts } from "../../../components/Common/HelperComponents/GetDataFromStore";

const normalizeStatus = (status) => String(status || "").toUpperCase();
const isOnboarded = (acc) => normalizeStatus(acc.status) === "ONBOARDED";
const isShadow = (acc) => normalizeStatus(acc.status) === "SHADOW";
const hasCompany = (acc) => Boolean((acc.company || "").trim());
const isSelectableAccount = (acc) => isShadow(acc) && !hasCompany(acc);

const CustomerModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const {
    isAddingCustomer,
    addCustomerSuccess,
    addCustomerError,
    isFetchingCustomerOrgAccounts,
    customerOrgAccountsSuccess,
    customerOrgAccountsError,
  } = useSelector((state) => state.customer);
  const { selectedProvider } = useContext(CloudProviderContext);
  const orgAccounts = useAccounts();
  const cloudTypeLookup = useMemo(
    () => buildAccountCloudTypeLookup(orgAccounts),
    [orgAccounts]
  );
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Get mspOrgCode from localStorage
  const getMspOrgCode = () => {
    try {
      const selectedOrganization = localStorage.getItem('selectedOrganization');
      if (selectedOrganization) {
        const orgData = JSON.parse(selectedOrganization);
        return orgData?.orgCode || '';
      }
    } catch (error) {
      console.error('Error getting mspOrgCode from localStorage:', error);
    }
    return '';
  };

  const mspOrgCode = getMspOrgCode();

  // Form State - Organization
  const [orgName, setOrgName] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [orgPrimaryName, setOrgPrimaryName] = useState("");
  const [orgPrimaryEmail, setOrgPrimaryEmail] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgCity, setOrgCity] = useState("");
  const [orgProvince, setOrgProvince] = useState("");
  const [orgZipcode, setOrgZipcode] = useState("");
  const [orgCountry, setOrgCountry] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phonenumberError, setPhonenumberError] = useState("");

  const [addressValidationStatus, setAddressValidationStatus] = useState(null);
  const [addressValidationMessage, setAddressValidationMessage] = useState('');

  // Customer Account Information step
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [hasRetrievedOrgAccounts, setHasRetrievedOrgAccounts] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL"); // ALL | ONBOARDED | CUSTOMER | UNASSIGNED
  const [payerFilter, setPayerFilter] = useState("ALL");
  const [accountsPage, setAccountsPage] = useState(0);
  const accountsRowsPerPage = 10;

  const customerOrgAccounts = React.useMemo(() => {
    const data = customerOrgAccountsSuccess?.data;
    if (!data || typeof data !== "object") return [];

    // API shape: { [accountNumber]: { account_number, company, status, account_name, payer_account_id, payer_account_name } }
    return Object.values(data)
      .filter(Boolean)
      .map((acc) => ({
        accountNumber: String(acc.account_number ?? ""),
        company: acc.company ?? "",
        status: String(acc.status ?? ""),
        accountName: String(acc.account_name ?? "").trim(),
        payerAccountId: String(acc.payer_account_id ?? "").trim(),
        payerAccountName: String(acc.payer_account_name ?? "").trim(),
      }))
      .filter((acc) => acc.accountNumber);
  }, [customerOrgAccountsSuccess]);

  const providerCustomerOrgAccounts = React.useMemo(
    () =>
      filterAccountsByCloudProvider(
        customerOrgAccounts,
        selectedProvider,
        cloudTypeLookup
      ),
    [customerOrgAccounts, selectedProvider, cloudTypeLookup]
  );

  const payerOptions = React.useMemo(() => {
    const payers = new Map();
    providerCustomerOrgAccounts.forEach((acc) => {
      if (!acc.payerAccountId || payers.has(acc.payerAccountId)) return;
      payers.set(acc.payerAccountId, {
        id: acc.payerAccountId,
        name: acc.payerAccountName || "",
      });
    });
    return Array.from(payers.values()).sort((a, b) => a.id.localeCompare(b.id));
  }, [providerCustomerOrgAccounts]);

  const payerFilterValue = payerOptions.some((payer) => payer.id === payerFilter)
    ? payerFilter
    : "ALL";

  const accountsInPayerScope = React.useMemo(() => {
    if (payerFilterValue === "ALL") return providerCustomerOrgAccounts;
    return providerCustomerOrgAccounts.filter(
      (acc) => acc.payerAccountId === payerFilterValue
    );
  }, [providerCustomerOrgAccounts, payerFilterValue]);

  const accountCounts = React.useMemo(() => {
    const counts = { onboarded: 0, customer: 0, unassigned: 0 };
    accountsInPayerScope.forEach((acc) => {
      if (isOnboarded(acc)) counts.onboarded += 1;
      else if (isShadow(acc) && hasCompany(acc)) counts.customer += 1;
      else if (isShadow(acc) && !hasCompany(acc)) counts.unassigned += 1;
    });
    return counts;
  }, [accountsInPayerScope]);

  const visibleAccounts = React.useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    return accountsInPayerScope.filter((acc) => {
      // filter bucket
      if (accountFilter === "ONBOARDED" && !isOnboarded(acc)) return false;
      if (accountFilter === "CUSTOMER" && !(isShadow(acc) && hasCompany(acc))) return false;
      if (accountFilter === "UNASSIGNED" && !(isShadow(acc) && !hasCompany(acc))) return false;

      // search by account number / company / account name / payer
      if (!q) return true;
      return (
        acc.accountNumber.toLowerCase().includes(q) ||
        (acc.company || "").toLowerCase().includes(q) ||
        (acc.accountName || "").toLowerCase().includes(q) ||
        (acc.payerAccountId || "").toLowerCase().includes(q) ||
        (acc.payerAccountName || "").toLowerCase().includes(q)
      );
    });
  }, [accountsInPayerScope, accountSearch, accountFilter]);

  const visibleSelectableAccountNumbers = React.useMemo(
    () => visibleAccounts.filter(isSelectableAccount).map((a) => a.accountNumber),
    [visibleAccounts]
  );

  const totalVisibleAccounts = visibleAccounts.length;
  const totalAccountPages = Math.max(1, Math.ceil(totalVisibleAccounts / accountsRowsPerPage));

  const pagedVisibleAccounts = React.useMemo(() => {
    const start = accountsPage * accountsRowsPerPage;
    return visibleAccounts.slice(start, start + accountsRowsPerPage);
  }, [visibleAccounts, accountsPage]);

  useEffect(() => {
    // Reset pagination on any filter/search/data change
    setAccountsPage(0);
  }, [accountSearch, accountFilter, payerFilter, customerOrgAccountsSuccess]);

  const toggleAccountFilter = (filterKey) => {
    setAccountFilter((prev) => (prev === filterKey ? "ALL" : filterKey));
  };

  const getFilterChipSx = (isActive) => ({
    height: 32,
    borderRadius: '999px',
    fontWeight: 700,
    fontSize: '0.75rem',
    cursor: 'pointer',
    transition: 'all 120ms ease',
    ...(isActive
      ? {
          backgroundColor: '#0f172a',
          color: '#ffffff',
          borderColor: '#0f172a',
          '& .MuiChip-icon': { color: '#ffffff' },
          '&:hover': { backgroundColor: '#111827' },
        }
      : {
          backgroundColor: '#ffffff',
          color: '#334155',
          borderColor: '#e2e8f0',
          '& .MuiChip-icon': { color: '#64748b' },
          '&:hover': { backgroundColor: '#f8fafc' },
        }),
  });

  const renderFilterLabel = (label, count) => `${label} (${count})`;

  // Handle retrieve organization click
  const handleRetrieveOrganization = () => {
    setHasRetrievedOrgAccounts(true);
    setSelectedAccounts([]);
    setAccountSearch("");
    setAccountFilter("ALL");
    setPayerFilter("ALL");
    setAccountsPage(0);
    dispatch(getCustomerOrgAccounts());
  };

  // Handle account selection
  const handleAccountSelection = (accountNumber, isSelected) => {
    if (isSelected) {
      setSelectedAccounts((prev) =>
        prev.includes(accountNumber) ? prev : [...prev, accountNumber]
      );
    } else {
      setSelectedAccounts((prev) => prev.filter((n) => n !== accountNumber));
    }
  };

  // Handle select all accounts
  const handleSelectAll = () => {
    const allSelected =
      visibleSelectableAccountNumbers.length > 0 &&
      visibleSelectableAccountNumbers.every((n) => selectedAccounts.includes(n));

    setSelectedAccounts(allSelected ? [] : visibleSelectableAccountNumbers);
  };

  // Clear form state
  const clearFormState = () => {
    setOrgName("");
    setOrgDescription("");
    setOrgPrimaryName("");
    setOrgPrimaryEmail("");
    setOrgAddress("");
    setOrgCity("");
    setOrgProvince("");
    setOrgZipcode("");
    setOrgCountry("");
    setPhoneNumber("");
    setPhonenumberError("");
    setAddressValidationStatus(null);
    setAddressValidationMessage('');
    setSelectedAccounts([]);
    setHasRetrievedOrgAccounts(false);
    setAccountSearch("");
    setAccountFilter("ALL");
    setPayerFilter("ALL");
    setAccountsPage(0);
    setConfirmDialogOpen(false);
  };

  // Validation function
  const isFormInvalid = () => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (
      !orgName ||
      !orgDescription ||
      !orgPrimaryName ||
      !orgPrimaryEmail ||
      !emailRegex.test(orgPrimaryEmail) ||
      !orgAddress ||
      !orgCountry ||
      addressValidationStatus === 'invalid' ||
      selectedAccounts.length === 0 ||
      (phoneNumber && !isPossiblePhoneNumber(phoneNumber))
    );
  };

  // Handle form submission - show confirmation dialog first
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!isFormInvalid()) {
      setConfirmDialogOpen(true);
    }
  };

  // Actually submit the form after confirmation
  const handleConfirmSubmit = () => {
    const newCustomer = {
      mspOrgCode,
      orgname: orgName,
      orgmetadata: "",
      orgdescription: orgDescription,
      orgprimaryname: orgPrimaryName,
      orgprimaryemail: orgPrimaryEmail,
      orgaddress: orgAddress,
      orgcity: orgCity,
      orgstate: orgProvince,
      orgcountry: orgCountry,
      orgzip: orgZipcode,
      orgbillingname: orgPrimaryName,
      orgbillingemail: orgPrimaryEmail,
      orgbillingaddress: orgAddress,
      orgbillingcity: orgCity,
      orgbillingstate: orgProvince,
      orgbillingcountry: orgCountry,
      orgbillingzip: orgZipcode,
      phoneNumber,
      accountNumbers: selectedAccounts,
    };
    console.log("🚀 ~ handleFormSubmit ~ newCustomer:", newCustomer);
    dispatch(addCustomer(newCustomer));
    setConfirmDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  // Handle phone number validation
  const handlePhoneBlur = () => {
    if (!phoneNumber) {
      setPhonenumberError("Phone number is required.");
    } else if (!isPossiblePhoneNumber(phoneNumber)) {
      setPhonenumberError("Please enter a valid phone number with country code (e.g., +1 555 123 4567).");
    } else {
      setPhonenumberError("");
    }
  };


  const validateAddress = async () => {
    if (!orgAddress.trim() || !orgCountry) return;

    setAddressValidationStatus(null);
    setAddressValidationMessage('Validating address...');

    const regionCode = getRegionCode(orgCountry);
    if (!regionCode) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Please select a valid country first');
      return;
    }

    try {
      const result = await validateAddressWithAPI(
        orgAddress, 
        regionCode, 
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );

      setAddressValidationStatus(result.isValid ? 'valid' : 'invalid');
      setAddressValidationMessage(result.message);
      
      setOrgAddress(result.formattedAddress || orgAddress);
      setOrgCity(result.city);
      setOrgProvince(result.state);
      setOrgZipcode(result.zip);
    } catch (error) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Failed to validate address. Please try again.');
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      clearFormState();
      dispatch(clearCustomerMessages());
      dispatch(resetCustomerOrgAccounts());
    }
    return () => {
      dispatch(clearCustomerMessages());
      dispatch(resetCustomerOrgAccounts());
    };
  }, [open, dispatch]);

  // Handle success response
  useEffect(() => {
    if (addCustomerSuccess) {
      setSnackbarMessage(addCustomerSuccess.message || "Customer created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
        clearFormState();
        onSuccess();
        dispatch(clearCustomerMessages());
        dispatch(resetCustomerOrgAccounts());
      }, 2000);
    }
  }, [addCustomerSuccess, onClose, onSuccess, dispatch]);

  // Handle error response
  useEffect(() => {
    if (addCustomerError) {
      setSnackbarMessage(addCustomerError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [addCustomerError]);

  // Handle customer org accounts error
  useEffect(() => {
    if (customerOrgAccountsError) {
      setSnackbarMessage(customerOrgAccountsError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [customerOrgAccountsError]);

  return (
    <>
      <Modal
        open={open}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key - only close via close button
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onClose();
        }}
        aria-labelledby="add-customer-modal"
        aria-describedby="modal-for-adding-customer"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 760 },
            maxWidth: 760,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
            overflow: 'hidden',
          }}
        >
          {/* Header - Matching CreateAssessmentModal style */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F5F5F5',
              padding: '16px 24px',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#000',
                fontSize: '1.125rem',
              }}
            >
              Add Customer
            </Typography>
            <IconButton 
              onClick={() => {
                clearFormState();
                onClose();
              }}
              sx={{ 
                color: '#000',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ 
            p: 3, 
            overflowY: 'auto',
            flex: 1,
            maxHeight: 'calc(90vh - 80px)',
          }}>
            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={2}>
                {/* Customer Information Section */}
                <Grid item xs={12}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 600, 
                      color: '#1e293b',
                      fontSize: '1rem',
                    }}
                  >
                    Customer Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer Name"
                    name="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    name="orgDescription"
                    value={orgDescription}
                    onChange={(e) => setOrgDescription(e.target.value)}
                    variant="outlined"
                    multiline
                    rows={3}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Primary Contact Name"
                    name="orgPrimaryName"
                    value={orgPrimaryName}
                    onChange={(e) => setOrgPrimaryName(e.target.value)}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Primary Contact Email"
                    name="orgPrimaryEmail"
                    value={orgPrimaryEmail}
                    onChange={(e) => setOrgPrimaryEmail(e.target.value)}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={countryList}
                    getOptionLabel={(option) => option?.name || ''}
                    value={countryList.find(country => country.name === orgCountry) || null}
                    onChange={(event, newValue) => {
                      setOrgCountry(newValue?.name || '');
                      setOrgAddress('');
                      setAddressValidationStatus(null);
                      setAddressValidationMessage('');
                    }}
                    isOptionEqualToValue={(option, value) => option.name === value?.name}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Country" 
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.code}>
                        {option.name}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onBlur={handlePhoneBlur}
                    variant="outlined"
                    placeholder="+1 (555) 123-4567"
                    required
                    error={!!phonenumberError}
                    helperText={phonenumberError || "Enter phone number with country code"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <div style={{ position: 'relative' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address"
                      value={orgAddress}
                      onChange={(e) => {
                        setOrgAddress(e.target.value);
                        setAddressValidationStatus(null);
                        setAddressValidationMessage('');
                      }}
                      onBlur={validateAddress}
                      variant="outlined"
                      required
                      disabled={!orgCountry}
                      placeholder={!orgCountry ? "Please select a country first" : "Enter complete address"}
                      error={addressValidationStatus === 'invalid'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.875rem',
                        },
                      }}
                      InputProps={{
                        endAdornment: addressValidationStatus === 'valid' && (
                          <i 
                            className="mdi mdi-check-circle"
                            style={{
                              position: 'absolute',
                              right: '10px',
                              color: '#059669'
                            }}
                          />
                        )
                      }}
                    />
                    {addressValidationMessage && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 0.5, 
                          display: 'block',
                          color: addressValidationStatus === 'valid' ? '#059669' : '#dc2626',
                          fontSize: '0.75rem',
                        }}
                      >
                        {addressValidationMessage}
                      </Typography>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    value={orgCity}
                    onChange={(e) => setOrgCity(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="State/Province"
                    value={orgProvince}
                    onChange={(e) => setOrgProvince(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ZIP/Postal Code"
                    value={orgZipcode}
                    onChange={(e) => setOrgZipcode(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>

                {/* Customer Account Information Section */}
                <Grid item xs={12}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2, 
                      mt: 3, 
                      fontWeight: 600, 
                      color: '#1e293b',
                      fontSize: '1rem',
                    }}
                  >
                    Customer Account Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" mb={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRetrieveOrganization}
                      disabled={isFetchingCustomerOrgAccounts}
                      startIcon={isFetchingCustomerOrgAccounts ? <CircularProgress size={20} /> : null}
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {isFetchingCustomerOrgAccounts ? "Retrieving Organization Accounts..." : "Retrieve Organization Accounts"}
                    </Button>
                  </Box>
                </Grid>

                {hasRetrievedOrgAccounts && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 3,
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Alert
                        severity="warning"
                        sx={{
                          mb: 3,
                          backgroundColor: '#fff7ed',
                          border: '1px solid #fed7aa',
                          color: '#9a3412',
                          '& .MuiAlert-icon': { color: '#ea580c' },
                        }}
                      >
                        Please ensure you have selected the correct account numbers for the selected customer. To undo this you will have to contact customer support.
                      </Alert>

                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#1e293b',
                              mb: 0.5,
                              fontSize: '0.875rem',
                            }}
                          >
                            Organization Accounts
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#64748b', fontSize: '0.75rem' }}
                          >
                            Select the AWS accounts to associate with this customer
                          </Typography>
                        </Box>
                      </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderColor: '#e2e8f0',
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            width: '100%',
                          }}
                        >
                          <TextField
                            size="small"
                            value={accountSearch}
                            onChange={(e) => setAccountSearch(e.target.value)}
                            placeholder="Search"
                            sx={{
                              flex: '1 1 180px',
                              minWidth: 0,
                              maxWidth: 280,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                                borderRadius: '999px',
                                backgroundColor: '#ffffff',
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                              ),
                            }}
                          />

                          <TextField
                            select
                            size="small"
                            label="Payer Account ID"
                            value={payerFilterValue}
                            onChange={(e) => setPayerFilter(e.target.value)}
                            sx={{
                              flex: '1 1 220px',
                              minWidth: 0,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.8125rem',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                              },
                              '& .MuiSelect-select': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: { sx: { maxHeight: 320 } },
                              },
                            }}
                          >
                            <MenuItem value="ALL" sx={{ fontSize: '0.8125rem' }}>
                              All
                            </MenuItem>
                            {payerOptions.map((payer) => (
                              <MenuItem
                                key={payer.id}
                                value={payer.id}
                                sx={{ fontSize: '0.8125rem' }}
                              >
                                {payer.name ? `${payer.id} · ${payer.name}` : payer.id}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            clickable
                            icon={accountFilter === "ONBOARDED" ? <RemoveIcon /> : <AddIcon />}
                            label={renderFilterLabel("Paired", accountCounts.onboarded)}
                            variant="outlined"
                            onClick={() => toggleAccountFilter("ONBOARDED")}
                            sx={getFilterChipSx(accountFilter === "ONBOARDED")}
                          />
                          <Chip
                            clickable
                            icon={accountFilter === "CUSTOMER" ? <RemoveIcon /> : <AddIcon />}
                            label={renderFilterLabel("Customer", accountCounts.customer)}
                            variant="outlined"
                            onClick={() => toggleAccountFilter("CUSTOMER")}
                            sx={getFilterChipSx(accountFilter === "CUSTOMER")}
                          />
                          <Chip
                            clickable
                            icon={accountFilter === "UNASSIGNED" ? <RemoveIcon /> : <AddIcon />}
                            label={renderFilterLabel("Unassigned", accountCounts.unassigned)}
                            variant="outlined"
                            onClick={() => toggleAccountFilter("UNASSIGNED")}
                            sx={getFilterChipSx(accountFilter === "UNASSIGNED")}
                          />
                        </Box>
                      </Box>
                    </Paper>

                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          borderColor: '#e2e8f0',
                          borderRadius: '8px',
                          overflow: 'auto',
                          maxHeight: 480,
                        }}
                      >
                        <Table stickyHeader size="small" aria-label="organization accounts table">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 48, backgroundColor: '#f8fafc' }}>
                                <Checkbox
                                  size="small"
                                  onChange={handleSelectAll}
                                  checked={
                                    visibleSelectableAccountNumbers.length > 0 &&
                                    visibleSelectableAccountNumbers.every((n) => selectedAccounts.includes(n))
                                  }
                                  indeterminate={
                                    visibleSelectableAccountNumbers.length > 0 &&
                                    visibleSelectableAccountNumbers.some((n) => selectedAccounts.includes(n)) &&
                                    !visibleSelectableAccountNumbers.every((n) => selectedAccounts.includes(n))
                                  }
                                  disabled={visibleSelectableAccountNumbers.length === 0}
                                  sx={{
                                    color: '#1e293b',
                                    '&.Mui-checked': { color: '#1e293b' },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                Account Number
                              </TableCell>
                              <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                Status
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedVisibleAccounts.map((acc) => {
                              const selectable = isSelectableAccount(acc);
                              const disabled = !selectable;
                              const checked = selectedAccounts.includes(acc.accountNumber);

                              return (
                                <TableRow key={acc.accountNumber} hover>
                                  <TableCell sx={{ width: 48 }}>
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      disabled={disabled}
                                      onChange={(e) => handleAccountSelection(acc.accountNumber, e.target.checked)}
                                      sx={{
                                        color: '#1e293b',
                                        '&.Mui-checked': { color: '#1e293b' },
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        fontFamily: 'monospace',
                                        backgroundColor: '#f8fafc',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        display: 'inline-block',
                                      }}
                                    >
                                      {acc.accountNumber}
                                    </Typography>
                                    {acc.accountName ? (
                                      <Typography
                                        sx={{
                                          display: 'block',
                                          mt: 0.5,
                                          fontSize: '0.75rem',
                                          color: '#64748b',
                                          maxWidth: 280,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {acc.accountName}
                                      </Typography>
                                    ) : null}
                                    {acc.payerAccountId ? (
                                      <Typography
                                        sx={{
                                          display: 'block',
                                          mt: 0.25,
                                          fontSize: '0.75rem',
                                          color: '#475569',
                                          fontFamily: 'monospace',
                                          maxWidth: 280,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                        title={
                                          acc.payerAccountName
                                            ? `Payer ${acc.payerAccountId} · ${acc.payerAccountName}`
                                            : `Payer ${acc.payerAccountId}`
                                        }
                                      >
                                        Payer {acc.payerAccountId}
                                        {acc.payerAccountName ? ` · ${acc.payerAccountName}` : ''}
                                      </Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
                                      {isOnboarded(acc) && (
                                        <Chip
                                          label={hasCompany(acc) ? acc.company : "PAIRED"}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            height: 20,
                                            color: '#64748b',
                                            borderColor: '#e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                          }}
                                        />
                                      )}
                                      {isShadow(acc) && hasCompany(acc) && (
                                        <Chip
                                          label={acc.company}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            height: 20,
                                            color: '#64748b',
                                            borderColor: '#e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                          }}
                                        />
                                      )}
                                      {isShadow(acc) && !hasCompany(acc) && (
                                        <Chip
                                          label="UNASSIGNED"
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            height: 20,
                                            color: '#64748b',
                                            borderColor: '#e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {totalVisibleAccounts === 0 && (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                    {isFetchingCustomerOrgAccounts
                                      ? "Retrieving accounts..."
                                      : hasRetrievedOrgAccounts
                                        ? "No accounts match your search/filters."
                                        : "No accounts returned. Click “Retrieve Organization Accounts” to load accounts."}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {hasRetrievedOrgAccounts && totalVisibleAccounts > 0 && (
                        <Box
                          mt={1.5}
                          pt={1.5}
                          pb={0.5}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1}
                          sx={{
                            color: '#64748b',
                            borderTop: '1px solid #f1f5f9',
                          }}
                        >
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#475569' }}>
                            Showing{" "}
                            <Box component="span" sx={{ color: '#1e293b', fontWeight: 600 }}>
                              {accountsPage * accountsRowsPerPage + 1}–
                              {Math.min((accountsPage + 1) * accountsRowsPerPage, totalVisibleAccounts)}
                            </Box>
                            {" "}of{" "}
                            <Box component="span" sx={{ color: '#1e293b', fontWeight: 600 }}>
                              {totalVisibleAccounts}
                            </Box>
                            {" "}account{totalVisibleAccounts !== 1 ? "s" : ""}
                          </Typography>
                          {totalAccountPages > 1 && (
                            <Pagination
                              size="small"
                              shape="rounded"
                              variant="outlined"
                              count={totalAccountPages}
                              page={accountsPage + 1}
                              onChange={(_, nextPage) => setAccountsPage(nextPage - 1)}
                              showFirstButton
                              showLastButton
                              siblingCount={1}
                              boundaryCount={0}
                              sx={{
                                '& .MuiPaginationItem-root': {
                                  borderColor: '#e2e8f0',
                                  color: '#475569',
                                  fontSize: '0.75rem',
                                  minWidth: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: '#f1f5f9',
                                    borderColor: '#cbd5e1',
                                  },
                                },
                                '& .Mui-selected': {
                                  backgroundColor: '#0f172a',
                                  color: '#ffffff',
                                  borderColor: '#0f172a',
                                  '&:hover': {
                                    backgroundColor: '#1e293b',
                                  },
                                },
                              }}
                            />
                          )}
                        </Box>
                      )}

                      {selectedAccounts.length > 0 && (
                        <Box
                          mt={3}
                          p={2}
                          sx={{
                            backgroundColor: '#d1fae5',
                            borderRadius: '8px',
                            border: '1px solid #a7f3d0',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#059669',
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#047857',
                                fontSize: '0.75rem',
                              }}
                            >
                              {selectedAccounts.length} account{selectedAccounts.length !== 1 ? 's' : ''} selected
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  color: 'text.secondary',
                }}
              >
                Note: If you would like to give access to your customer, go to the customer&apos;s dashboard and add users in the configuration module.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    clearFormState();
                    onClose();
                  }}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isFormInvalid() || isAddingCustomer}
                  startIcon={isAddingCustomer ? <CircularProgress size={20} /> : null}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {isAddingCustomer ? "Adding Customer..." : "Add Customer"}
                </Button>
              </Box>
            </form>
          </Box>
        </Paper>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Confirm Customer Creation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" sx={{ color: '#1e293b', mb: 2 }}>
            Please ensure you have selected the correct account numbers for the selected customer. To undo this you will have to contact customer support.
          </DialogContentText>
          <Alert severity="warning" sx={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
            You are about to create a customer with {selectedAccounts.length} account{selectedAccounts.length !== 1 ? 's' : ''}. This action is irreversible.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            color="primary"
            autoFocus
            disabled={isAddingCustomer}
            startIcon={isAddingCustomer ? <CircularProgress size={20} /> : null}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {isAddingCustomer ? "Adding Customer..." : "Confirm & Add Customer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

CustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CustomerModal;

